import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  map,
  timeout,
  switchMap,
  forkJoin,
  of,
  catchError,
  shareReplay,
  Observable,
} from 'rxjs';
import { libraryScreenshotUrl } from '../../nudge-v2/nudge-library/nudge-library-payload';
export const AUDIENCE_OPTIONS = [
  { id: 'strong', label: 'Strong matches only', threshold: 70 },
  { id: 'moderate', label: 'Strong and moderate matches', threshold: 50 },
  { id: 'broad', label: 'Include weaker matches too', threshold: 30 },
];
export function audienceLabel(score: number): string {
  return (
    AUDIENCE_OPTIONS.find((p) => p.threshold === score)?.label ||
    'Custom audience'
  );
}
// Older saved audiences sometimes contain the event key instead of a display label.
export function segmentDisplayName(value: string | null | undefined): string {
  const name = (value || '').trim();
  return /^[a-z0-9]+(?:[_-][a-z0-9]+)+$/.test(name)
    ? name.split(/[_-]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    : name;
}
export interface SignalsContext {
  target: string;
  stage: string;
  segment: string;
  name: string;
  description?: string;
  threshold?: number;
  action?: string;
  campaignId?: string;
  addNudges?: boolean;
  reviewOnReturn?: boolean;
}
@Injectable({ providedIn: 'root' })
export class SignalsExperimentsService {
  private previews = new Map<string, Observable<any>>();
  private base = '/behavioral-intelligence/experiments';
  constructor(private http: HttpClient) {}
  get(path: string, params: Record<string, string>) {
    return this.http.get<any>(this.base + path, { params }).pipe(
      timeout(45000),
      map((r) => this.unwrap(r))
    );
  }
  post(path: string, body: any) {
    return this.http.post<any>(this.base + path, body).pipe(
      timeout(path === '/reach' || path === '/suggestions' ? 240000 : 60000),
      map((r) => this.unwrap(r)),
      switchMap((r) =>
        path === '/suggestions' && r.suggestions?.length
          ? forkJoin(
              r.suggestions.map((n: any) => this.withLibraryImage(n))
            ).pipe(map((suggestions) => ({ ...r, suggestions })))
          : of(r)
      )
    );
  }
  private withLibraryImage(n: any): Observable<any> {
    if (n.generated) return of({ ...n, image: '' });
    if (n.sourceTarget && n.incentiveId)
      return of({ ...n, image: libraryScreenshotUrl(n) });
    if (!n.libraryId) return of({ ...n, image: '' });
    // Older API versions omit source identifiers. Resolve once, using the same library record.
    if (!this.previews.has(n.libraryId)) {
      if (this.previews.size >= 100) this.previews.clear();
      this.previews.set(
        n.libraryId,
        this.http
          .get<any>('/nudge-catalog/' + encodeURIComponent(n.libraryId))
          .pipe(timeout(15000), shareReplay(1))
      );
    }
    return this.previews.get(n.libraryId)!.pipe(
      map((source) => ({ ...n, image: libraryScreenshotUrl(source) })),
      catchError(() => of({ ...n, image: '' }))
    );
  }
  private unwrap(r: any) {
    if (!r?.success)
      throw new Error(r?.message || 'The request could not be completed.');
    return r.data;
  }
}
